/**
 * App-wide font size presets. Applied as a percentage on the root `<html>` element rather than
 * through `typography.fontSize`: every MUI size (type scale, SvgIcon, IconButton, Chip, inputs,
 * table cells) is expressed in rem through `pxToRem`, so scaling the root scales all of them at
 * once and the theme object never has to be rebuilt. A percentage also keeps a user's own browser
 * font-size preference in the calculation, where a px value would override it.
 */
export interface FontSizeOption {
  id: string;
  label: string;
  /** Multiplier on the browser's root size (1 = today's default). */
  scale: number;
}

export const FONT_SIZE_STORAGE_KEY = 'zuul-font-size';
export const DEFAULT_FONT_SIZE_ID = 'default';

// Keep in sync with the pre-paint script in index.html, which mirrors these four values so the
// page never paints at one size and snaps to another once the bundle loads.
export const FONT_SIZE_OPTIONS: readonly FontSizeOption[] = [
  { id: 'small', label: 'Small', scale: 0.875 },
  { id: DEFAULT_FONT_SIZE_ID, label: 'Default', scale: 1 },
  { id: 'large', label: 'Large', scale: 1.125 },
  { id: 'x-large', label: 'Extra large', scale: 1.25 },
];

export const DEFAULT_FONT_SIZE: FontSizeOption = FONT_SIZE_OPTIONS[1]!;

export const getFontSize = (id: string | null | undefined): FontSizeOption =>
  FONT_SIZE_OPTIONS.find((option) => option.id === id) ?? DEFAULT_FONT_SIZE;

/** Reads the persisted size id; unknown or unreadable values fall back to the default. */
export const readStoredFontSizeId = (): string => {
  try {
    return getFontSize(localStorage.getItem(FONT_SIZE_STORAGE_KEY)).id;
  } catch {
    return DEFAULT_FONT_SIZE_ID;
  }
};

/** Writes the size onto `<html>`; the same statement the pre-paint script runs before React mounts. */
export const applyFontSize = (option: FontSizeOption): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.fontSize = `${option.scale * 100}%`;
};
